import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { X, Stethoscope, Clock, Euro, AlertTriangle } from 'lucide-react';
import { appointmentsApi } from '../services/api';
import { Intervention } from '../types';

interface InterventionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<Intervention>) => void;
  intervention?: Intervention | null;
  patientId?: string;
  isLoading?: boolean;
}

const InterventionModal: React.FC<InterventionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  intervention,
  patientId,
  isLoading = false,
}) => {
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<Partial<Intervention> & { materials?: string }>();

  // Fetch patient appointments for selection
  const { data: appointmentsResponse } = useQuery(
    ['patient-appointments', patientId],
    () => appointmentsApi.getAll({ patient: patientId }),
    {
      enabled: !!patientId && !intervention,
    }
  );

  const appointments = appointmentsResponse?.data || [];

  useEffect(() => {
    if (intervention) {
      reset({
        type: intervention.type,
        description: intervention.description,
        procedure: intervention.procedure,
        materials: intervention.materials?.join(', '),
        cost: intervention.cost,
        status: intervention.status,
        complications: intervention.complications,
        followUpRequired: intervention.followUpRequired,
        followUpDate: intervention.followUpDate ? intervention.followUpDate.split('T')[0] : '',
        dentist: intervention.dentist,
        assistant: intervention.assistant,
        duration: intervention.duration,
        anesthesia: intervention.anesthesia,
        notes: intervention.notes,
        appointment: intervention.appointment,
        tooth: intervention.tooth || {},
      });
    } else {
      reset({
        status: 'planned',
        anesthesia: 'none',
        followUpRequired: false,
        tooth: {},
      });
    }
  }, [intervention, reset]);

  const onSubmit = (data: Partial<Intervention>) => {
    const materialsArray: string[] = data.materials ? 
      (typeof data.materials === 'string' ? 
        (data.materials as string).split(',').map((m: string) => m.trim()).filter((m: string) => m) : 
        (data.materials as string[])) : [];

    const submitData = {
      ...data,
      materials: materialsArray,
      patient: patientId,
    };

    onSave(submitData);
  };

  const selectedAppointmentId = watch('appointment');
  const selectedAppointment = appointments.find((a: any) => a._id === selectedAppointmentId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full max-w-sm md:max-w-2xl lg:max-w-4xl mx-auto">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {intervention ? 'Editar Intervención' : 'Nueva Intervención'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Appointment Selection (only for new interventions) */}
                {!intervention && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cita Asociada
                    </label>
                    <select
                      {...register('appointment')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    >
                      <option value="">Seleccionar cita</option>
                      {appointments.map((appointment: { _id: React.Key | readonly string[] | null | undefined; startTime: string | number | Date; title: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; }) => {
                        const id = Array.isArray(appointment._id) ? appointment._id.join(',') : (appointment._id ?? '').toString();
                        return (
                          <option key={id} value={id}>
                            {new Date(appointment.startTime).toLocaleDateString('es-ES')} - {appointment.title}
                          </option>
                        );
                      })}
                    </select>
                    {selectedAppointment && (
                      <div className="mt-2 p-2 bg-gray-50 rounded-md">
                        <p className="text-sm text-gray-600">
                          <strong>Fecha:</strong> {new Date(selectedAppointment.startTime).toLocaleDateString('es-ES')}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Dentista:</strong> {selectedAppointment.dentist}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Type and Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Intervención *
                    </label>
                    <select
                      {...register('type', { required: 'El tipo es requerido' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    >
                      <option value="cleaning">Limpieza</option>
                      <option value="filling">Empaste</option>
                      <option value="extraction">Extracción</option>
                      <option value="root_canal">Endodoncia</option>
                      <option value="crown">Corona</option>
                      <option value="bridge">Puente</option>
                      <option value="implant">Implante</option>
                      <option value="orthodontics">Ortodoncia</option>
                      <option value="whitening">Blanqueamiento</option>
                      <option value="consultation">Consulta</option>
                      <option value="emergency">Emergencia</option>
                      <option value="other">Otro</option>
                    </select>
                    {errors.type && (
                      <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estado
                    </label>
                    <select
                      {...register('status')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    >
                      <option value="planned">Planificada</option>
                      <option value="in_progress">En Progreso</option>
                      <option value="completed">Completada</option>
                      <option value="cancelled">Cancelada</option>
                    </select>
                  </div>
                </div>

                {/* Description and Procedure */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción *
                  </label>
                  <textarea
                    {...register('description', { required: 'La descripción es requerida' })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    placeholder="Descripción detallada de la intervención..."
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Procedimiento *
                  </label>
                  <textarea
                    {...register('procedure', { required: 'El procedimiento es requerido' })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    placeholder="Procedimiento realizado..."
                  />
                  {errors.procedure && (
                    <p className="mt-1 text-sm text-red-600">{errors.procedure.message}</p>
                  )}
                </div>

                {/* Tooth Information */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <Stethoscope className="h-4 w-4 mr-2" />
                    Información del Diente
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Número del Diente
                      </label>
                      <input
                        type="text"
                        {...register('tooth.number')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                        placeholder="11, 21, etc."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Superficie
                      </label>
                      <select
                        {...register('tooth.surface')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                      >
                        <option value="">Seleccionar</option>
                        <option value="mesial">Mesial</option>
                        <option value="distal">Distal</option>
                        <option value="occlusal">Oclusal</option>
                        <option value="lingual">Lingual</option>
                        <option value="vestibular">Vestibular</option>
                        <option value="incisal">Incisal</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cuadrante
                      </label>
                      <select
                        {...register('tooth.quadrant')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                      >
                        <option value="">Seleccionar</option>
                        <option value="upper_right">Superior Derecho</option>
                        <option value="upper_left">Superior Izquierdo</option>
                        <option value="lower_left">Inferior Izquierdo</option>
                        <option value="lower_right">Inferior Derecho</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Treatment Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dentista *
                    </label>
                    <input
                      type="text"
                      {...register('dentist', { required: 'El dentista es requerido' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                      placeholder="Dr. Nombre"
                    />
                    {errors.dentist && (
                      <p className="mt-1 text-sm text-red-600">{errors.dentist.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asistente
                    </label>
                    <input
                      type="text"
                      {...register('assistant')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                      placeholder="Nombre del asistente"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duración (min)
                    </label>
                    <input
                      type="number"
                      {...register('duration', { min: 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                      placeholder="60"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Costo (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('cost', { min: 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Anestesia
                    </label>
                    <select
                      {...register('anesthesia')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    >
                      <option value="none">Ninguna</option>
                      <option value="local">Local</option>
                      <option value="general">General</option>
                      <option value="sedation">Sedación</option>
                    </select>
                  </div>
                </div>

                {/* Materials */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Materiales Utilizados
                  </label>
                  <input
                    type="text"
                    {...register('materials')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    placeholder="Separar materiales con comas (ej: composite, resina, etc.)"
                  />
                </div>

                {/* Complications */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Complicaciones
                  </label>
                  <textarea
                    {...register('complications')}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    placeholder="Describir cualquier complicación durante el tratamiento..."
                  />
                </div>

                {/* Follow-up */}
                <div className="border-t pt-4">
                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      {...register('followUpRequired')}
                      className="h-4 w-4 text-dental-600 focus:ring-dental-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Requiere seguimiento
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de Seguimiento
                    </label>
                    <input
                      type="date"
                      {...register('followUpDate')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas Adicionales
                  </label>
                  <textarea
                    {...register('notes')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dental-500 focus:border-dental-500"
                    placeholder="Notas adicionales sobre la intervención..."
                  />
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-dental-600 text-base font-medium text-white hover:bg-dental-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dental-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="spinner mr-2"></div>
                ) : null}
                {intervention ? 'Actualizar' : 'Crear'} Intervención
              </button>
              
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dental-500 sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InterventionModal;















